import axios from "axios";

const profile = window._env_?.activeProfile;
const config = window._env_?.profiles?.[profile];
const API_URL = config?.API_URL;
const GRAPHQL_URL = config?.GRAPHQL_URL;

const AUTH_SERVICE_URL = config?.AUTH_SERVICE_URL;
const BASIC_AUTH_TOKEN = config?.BASIC_AUTH_TOKEN;
const REQUIRE_AUTH = config?.REQUIRE_AUTH === "true";

let cachedToken = null;
let tokenExpiry = null;

async function getAuthToken(forceRefresh = false) {
  const now = Date.now();

  if (!forceRefresh && cachedToken && tokenExpiry && now < tokenExpiry) {
    return cachedToken;
  }

  try {
    console.log("Fetching new auth token...");
    const response = await axios.get(`${AUTH_SERVICE_URL}`, {
      headers: {
        Authorization: `Basic ${BASIC_AUTH_TOKEN}`,
      },
      responseType: "text", // Always request token as text
    });

    const rawData = response.data.trim();

    let token, tokenLife;

    try {
      // Try JSON format
      const json = JSON.parse(rawData);
      token = json.token;
      tokenLife = json.tokenLife;
    } catch (jsonError) {
      // Try XML format
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(rawData, "application/xml");
      token =
        xmlDoc.getElementsByTagName("ns2:token")[0]?.textContent ||
        xmlDoc.getElementsByTagName("token")[0]?.textContent;
      tokenLife =
        xmlDoc.getElementsByTagName("ns2:tokenLife")[0]?.textContent ||
        xmlDoc.getElementsByTagName("tokenLife")[0]?.textContent;
    }

    if (!token) {
      throw new Error("No token found in auth service response");
    }

    console.log("Auth token fetched successfully:", token);

    cachedToken = token;
    tokenExpiry = now + parseInt(tokenLife, 10) * 1000 - 5000;

    return cachedToken;
  } catch (error) {
    console.error("Failed to fetch auth token", error);
    throw error;
  }
}

export async function fetchWithAuth({
  method = "GET",
  endpoint,
  data = null,
  params = null,
  headers = {},
  responseType = "json",
}) {
  try {
    const requestHeaders = { ...headers };

    if (REQUIRE_AUTH) {
      const token = await getAuthToken();
      requestHeaders.Authorization = `Bearer ${token}`;
    }

    // Let axios set multipart boundary for FormData uploads (e.g. S3 bulk upload).
    if (typeof FormData !== "undefined" && data instanceof FormData) {
      delete requestHeaders["Content-Type"];
      delete requestHeaders["content-type"];
    }

    const response = await axios({
      method,
      url: `${API_URL}${endpoint}`,
      headers: requestHeaders,
      data,
      params,
      responseType,
    });

    return response.data;
  } catch (error) {
    console.error(`API call failed: ${method} ${endpoint}`, error);
    throw error;
  }
}

/**
 * POST a GraphQL document to GRAPHQL_URL (from env-config).
 * Uses the same bearer token flow as fetchWithAuth when REQUIRE_AUTH is true.
 *
 * @param {{ query: string, variables?: object, operationName?: string }} args
 * @returns {Promise<object>} The `data` object from the GraphQL response
 */
export async function graphqlWithAuth({ query, variables = null, operationName = null }) {
  if (!GRAPHQL_URL) {
    throw new Error(
      "GRAPHQL_URL is not set for the active profile in public/env-config.js"
    );
  }

  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };

  if (REQUIRE_AUTH) {
    const token = await getAuthToken();
    headers.Authorization = `Bearer ${token}`;
  }

  const body = { query };
  if (variables != null) {
    body.variables = variables;
  }
  if (operationName) {
    body.operationName = operationName;
  }

  try {
    const response = await axios.post(GRAPHQL_URL, body, { headers });
    const payload = response.data;

    if (payload.errors?.length) {
      const msg = payload.errors.map((e) => e.message).join("; ");
      throw new Error(msg || "GraphQL error");
    }

    return payload.data ?? {};
  } catch (error) {
    console.error("GraphQL request failed", error);
    throw error;
  }
}
